export interface SalePage<T> { data: T[]; total: number; page: number; pageSize: number }
export interface SaleLink {
  id: number; uploadId: number; linkCode: string; title: string; description: string;
  contact: string; remark: string; status: 'ACTIVE' | 'PAUSED' | 'REVOKED';
  saleUrl: string; fileName: string; contentType: string; createTime: string;
  codeCount: number; activatedCount: number; accessCount: number; lastAccessTime: string | null;
}
export interface SaleCode {
  id: number; linkId: number; batchId: number; batchName?: string; codeValue: string;
  status: 'UNUSED' | 'ACTIVATED' | 'DISABLED' | 'REPLACED'; deviceLabel: string | null;
  activatedAt: string | null; deliveredAt: string | null; lastAccessTime: string | null;
  accessCount: number; remark: string; replacementId: number | null; createTime: string;
  bindingMode?: 'DEVICE' | 'ACCOUNT' | null; buyerEmail?: string | null;
}
export interface SaleBatch { id: number; name: string; remark: string; quantity: number; createTime: string }
export interface SaleEvent { id: number; codeId: number; eventType: string; detail: string; ipHint: string; createTime: string }
export interface SalePolicy {
  sellerEnabled: boolean;
  creationEnabled: boolean;
  maxBatchSize: number;
  saleOrigin?: string | null;
}
export interface SalePublicInfo { title: string; description: string; contact: string; status: string; accountEnabled?: boolean }
export interface SaleBuyerSession {
  accountEnabled: boolean; authenticated: boolean; email: string | null; expiresAt: string | null;
  resendCooldownSeconds: number;
}
export interface SaleCodeDelivery { challengeId: string; expiresAt: string; retryAfterSeconds: number }
export interface SaleOwnedResource {
  codeId: number; linkId: number; linkCode: string; title: string; codeStatus: string;
  linkStatus: string; activatedAt: string; contentType: string | null; saleUrl: string; available: boolean;
}
export const BUYER_ACCOUNT_API = '/public/accounts';
// Navigation stays local even when an older backend returns an absolute URL for another server.
export const saleBuyerPath = (linkCode: string): string => `/sale/${encodeURIComponent(linkCode)}`;
export const saleShareUrl = (linkCode: string, saleOrigin?: string | null): string => {
  let origin = window.location.origin;
  if (saleOrigin) {
    try {
      origin = new URL(saleOrigin).origin;
    } catch {
      // Fall back to the current site when a stale policy has an invalid origin.
    }
  }
  return new URL(saleBuyerPath(linkCode), origin).href;
};
export interface SaleResource {
  uploadId: number; fileName: string; contentType: string; category: 'html' | 'image' | 'video' | 'audio' | 'file';
  htmlTitle?: string | null; url: string; storageType: string; expireTime: string | null;
}
export interface SaleLinkInput { uploadId?: number; title: string; description?: string; contact?: string; remark?: string }
export class SaleApiError extends Error {
  constructor(public code: string, message: string) { super(message); }
}

const BASE = '/img/sale-links';
export async function saleRequest<T>(path: string, method = 'GET', body?: unknown, secret?: string): Promise<T> {
  const response = await fetch(BASE + path, {
    method, credentials: 'same-origin', cache: 'no-store',
    headers: { 'Content-Type': 'application/json', ...(secret ? { 'X-Sale-Device': secret } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new SaleApiError(data.code || 'sale.requestFailed', data.message || 'Request failed');
  return data.data as T;
}

export const formatSaleCode = (value: string): string => value.replace(/(.{5})(?!$)/g, '$1-');

export function newSaleRequestId(): string {
  return randomSecret();
}

function randomSecret(): string {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join(''))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Persist before redeeming so a lost HTTP response cannot strand an activation. */
export function saleBrowserSecret(linkCode: string, create: boolean): string | null {
  const key = `img_sale_device:${linkCode}`;
  const existing = window.localStorage.getItem(key);
  if (existing && /^[A-Za-z0-9_-]{43}$/.test(existing)) return existing;
  if (!create) return null;
  const value = randomSecret();
  window.localStorage.setItem(key, value);
  if (window.localStorage.getItem(key) !== value) throw new Error('sale.storageUnavailable');
  return value;
}

export async function downloadSaleBatch(linkId: number, batchId: number): Promise<void> {
  const response = await fetch(`${BASE}/${linkId}/codes/export?batchId=${batchId}`, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) {
    const data = await response.json();
    throw new SaleApiError(data.code || 'sale.requestFailed', data.message || 'Export failed');
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = `sale-codes-${batchId}.csv`;
  document.body.appendChild(anchor); anchor.click(); anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
